/**
 * Main's self profile, from and to `/me` (MX-12, MX-13).
 *
 * Main moved "who am I" to keyon's `AccountUser.GetSelfProfile` /
 * `UpdateSelfProfile` (#315 via #321): the sessionSelf store, My Profile, the
 * order popup's prescriber prefill and every `sessionActorFrom` caller read the
 * shape `utils/selfProfileForm.js` describes. The port has no keyon; the same
 * person is `GET /me`, answered for the access token's subject (decision D4).
 * This file is the ONE mapping between the two, used by the bridge's two
 * entries and by the sessionSelf store, so the profile page and the session
 * identity cannot disagree about who is signed in.
 *
 * Pure: no axios, no stores.
 */

/** `users.phone_type` ⇄ main's `user_phone_type` (1 CELL, 2 HOME, 3 WORK). */
const PHONE_TYPE_TO_MAIN = { mobile: 1, home: 2, work: 3 };
const PHONE_TYPE_FROM_MAIN = { 1: 'mobile', 2: 'home', 3: 'work' };

/**
 * The communication preference in main's words. `mail` is a port value main's
 * select never offered; it is carried as 'Mail' so a save of an untouched
 * profile sends back what was read rather than quietly switching to e-mail.
 */
const PREFERENCE_TO_MAIN = { email: 'Email', sms: 'SMS', phone: 'Phone', all: 'All', mail: 'Mail' };
const PREFERENCE_FROM_MAIN = { email: 'email', sms: 'sms', phone: 'phone', all: 'all', mail: 'mail' };

/** `users.gender` ⇄ main's option values. `unknown` is "not chosen" on the page. */
const GENDER_TO_MAIN = { male: 'Male', female: 'Female', other: 'Other' };
const GENDER_FROM_MAIN = { male: 'male', female: 'female', other: 'other' };

function str(value) {
  return value === undefined || value === null ? '' : String(value).trim();
}

/** An emptied field is a cleared one; the API stores NULL, not ''. */
function orNull(value) {
  const text = str(value);

  return text === '' ? null : text;
}

function npiOf(me) {
  if (me.npi !== undefined && me.npi !== null) return str(me.npi);

  const npi = (Array.isArray(me.licenses) ? me.licenses : []).find((licence) => licence && licence.type === 'npi');

  return npi ? str(npi.number) : '';
}

/**
 * `GET /me` (or the answer to `PATCH /me`) as main's GetSelfProfile data.
 *
 * `unique_ID` is the user id and `accountnumber` the organization's number —
 * the same pair `useSessionStore()` answers with (`userId`,
 * `organizationNumber`), and for the same reason: legacy call sites use them
 * as a presence check and as ids the bridge addresses people by, never as
 * authority, which the token carries.
 */
export function meToSelfProfile(me) {
  const m = me && typeof me === 'object' ? me : {};
  const org = m.organization || {};

  return {
    accountnumber: str(org.number ?? org.id),
    unique_ID: str(m.id),
    account_type: str(m.account_type),
    username: str(m.username),
    firstName: str(m.first_name),
    lastName: str(m.last_name),
    dob: str(m.date_of_birth).slice(0, 10),
    gender: GENDER_TO_MAIN[m.gender] || '',
    npi: npiOf(m),
    phone1: str(m.phone),
    phone1Type: PHONE_TYPE_TO_MAIN[m.phone_type] || 0,
    phone2: str(m.phone_secondary),
    phone2Type: PHONE_TYPE_TO_MAIN[m.phone_secondary_type] || 0,
    email: str(m.email),
    communicationPreference: PREFERENCE_TO_MAIN[m.communication_preference] || 'Email',
    address: str(m.address_line1),
    addr2: str(m.address_line2),
    city: str(m.city),
    state: str(m.state),
    zip: str(m.postal_code),
    maddr: str(m.mailing_address_line1),
    maddr2: str(m.mailing_address_line2),
    mcity: str(m.mailing_city),
    mstate: str(m.mailing_state),
    mzip: str(m.mailing_postal_code),
    // Main offers gender to a Patient only, and the API refuses it for
    // anyone else.
    can_edit_gender: m.account_type === 'patient',
    // Main let a Provider or Administrator type their own NPI. In the port an
    // NPI is a credential an administrator provisions, and order and directive
    // signing trust it, so the page shows it and never sends it.
    can_edit_npi: false,
  };
}

/**
 * Main's UpdateSelfProfile `profile` (utils/selfProfileForm.js
 * buildSelfProfileWritePayload) as the PATCH /me body.
 *
 * Never sent: identity (the token says who), e-mail (read-only on main's page
 * too; sign-in identity belongs to pacmny-auth) and the NPI (see above).
 * Gender only for a patient, whose field it is; anyone else's page does not
 * show it, and the API would refuse it. A phone type the page could not show
 * (`other`, read back as 0) is left alone rather than overwritten.
 *
 * @param {object} profile main's write payload
 * @param {{isPatient?: boolean}} who the signed-in person, from the session
 */
export function selfProfileToMePatch(profile, { isPatient = false } = {}) {
  const p = profile && typeof profile === 'object' ? profile : {};
  const body = {
    first_name: str(p.firstName),
    last_name: str(p.lastName),
    date_of_birth: orNull(p.dob),
    phone: orNull(p.phone1),
    phone_secondary: orNull(p.phone2),
    address_line1: orNull(p.address),
    address_line2: orNull(p.addr2),
    city: orNull(p.city),
    state: orNull(p.state),
    postal_code: orNull(p.zip),
    mailing_address_line1: orNull(p.maddr),
    mailing_address_line2: orNull(p.maddr2),
    mailing_city: orNull(p.mcity),
    mailing_state: orNull(p.mstate),
    mailing_postal_code: orNull(p.mzip),
  };

  const phoneType = PHONE_TYPE_FROM_MAIN[Number(p.phone1Type)];
  if (phoneType) body.phone_type = phoneType;

  const secondaryType = PHONE_TYPE_FROM_MAIN[Number(p.phone2Type)];
  if (secondaryType) body.phone_secondary_type = secondaryType;

  const preference = PREFERENCE_FROM_MAIN[str(p.communicationPreference).toLowerCase()];
  if (preference) body.communication_preference = preference;

  if (isPatient) {
    const gender = GENDER_FROM_MAIN[str(p.gender).toLowerCase()];
    if (gender) body.gender = gender;
  }

  return body;
}

/**
 * A refused save in main's failure envelope, with the API's code and its
 * per-field reasons kept: My Profile tells "your sign-in number is changed at
 * sign-in" (409 `mfa_destination_managed_at_idp`) and "this date is in the
 * future" (422) apart from a generic failure, which a bare message cannot.
 */
export function selfProfileRefusal(error) {
  const details = error && error.details && typeof error.details === 'object' ? error.details : null;

  return {
    status: 'Error',
    code: (error && error.code) || 'error',
    message: (error && error.message) || 'Could not save profile',
    errors: error && error.code === 'validation_failed' ? details : null,
    data: null,
  };
}
